import * as React from "react";
import FalconHeader from "../../../../components/navigation/FalconHeader";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {
    ICacheManager,
    IFalconPropositionEngine,
    InteractionHelper,
    productHelper,
    ProductsEnum,
    Quote,
    Types
} from "@ferry/journey-services";
import {FalconNavigatorHeader, resourcesHelper, Footer} from "@ferry/portal-core";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {ListItem} from "@athena-portal/journey-model";
import {ILookupService, Types as JourneyCoreTypeDefs} from "@athena-portal/plugin-journey";
import {
    ConfirmModal,
    getInteractionVehicleFuelType,
    getVehicleEngineSize,
    getVehicleNumberOfDoors
} from "@ferry/plugin-ferry-journey";
import {StorageSuccessModal} from "../../../../components/modals/StorageSuccessModal";
import {getAmountWithDecimalDigits} from "../../../../CommonFunctions";
import * as moment from "moment/moment";
import {Types as CustomerPortalTypes} from "../../../../Types";
import type {ICustomerPortalService} from "../../../../services/ICustomerPortalService";

interface IGetNewQuotePageProps {
    isActivePoliciesPresent: boolean;
}

enum Modals {
    ALLIANZ = "ALLIANZ",
    LV = "LV"
}

type AllProducts = ProductsEnum;

interface IGetNewQuotePageState {
    showModal?: Modals;
    selectedProduct?: AllProducts;
    annualMotorQuotes: Quote[];
    quoteLimit: number;
    loading: boolean;
    showQuoteDeleteQuestionModal: boolean;
    showSuccessModal: boolean;
    interactionIdToUpdate: string;
    lookup: {[key: string]: ListItem[]}
    driveAwayQuoteInteraction: any;
}

// noinspection JSUnresolvedReference
@authorize
export class GetNewQuotePagePlain extends React.Component<IWithRouterProps & IGetNewQuotePageProps, IGetNewQuotePageState> {

    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    @lazyInject(Types.FalconPropositionEngine)
    private readonly propositionEngine: IFalconPropositionEngine;

    @lazyInject(Types.CacheManager)
    private readonly cacheManager: ICacheManager;

    @lazyInject(JourneyCoreTypeDefs.LookupService)
    private readonly lookupService: ILookupService;

    private readonly pluginTemplates = new PluginTemplates();
    private readonly DATE_FORMAT: string = "D MMM YYYY";

    constructor(props: Readonly<IWithRouterProps & IGetNewQuotePageProps>) {
        super(props);
        this.state = {
            showModal: undefined,
            selectedProduct: undefined,
            annualMotorQuotes: [],
            quoteLimit: 3,
            loading: true,
            showQuoteDeleteQuestionModal: false,
            showSuccessModal: false,
            interactionIdToUpdate: "",
            lookup: {},
            driveAwayQuoteInteraction: null
        };

        this.confirmQuoteDeleteAndCloseDeleteQuoteQuestionModal = this.confirmQuoteDeleteAndCloseDeleteQuoteQuestionModal.bind(this);
        this.showQuoteDeleteQuestionModal = this.showQuoteDeleteQuestionModal.bind(this);
        this.navigateTo = this.navigateTo.bind(this);
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Get a new quote");
        this.customerPortalService.getAllQuotes().subscribe((quotes: Quote[]) => {
            this.cacheManager.clearCache("getAllQuotes");
            this.fetchLookupValues("vehicleTransmission");
            this.fetchLookupValues("vehicleFuelType");
            this.setState({
                annualMotorQuotes: this.getQuotesByProduct(quotes, ProductsEnum.WOLF_MOTOR),
                loading: false
            });
        });
    }

    public render = () => {
        return (
            <>
                <FalconHeader/>
                <div id="noPaddingBottomPlugin" className="plugin">
                    {this.state.annualMotorQuotes.length > 0 ?
                        <>
                            <FalconNavigatorHeader back={this.getBackLink()} title="Your car insurance quotes"/>
                            {this.renderQuoteChoice()}
                        </>
                        : <FalconNavigatorHeader back={this.getBackLink()} title="There's nothing to see here... yet"/>
                    }

                    <div className="form-group">
                        <button type="button"
                                className="btn btn-primary d-block"
                                onClick={() => this.openModal(ProductsEnum.WOLF_MOTOR)}
                                id="getNewQuote">
                            Get a new quote
                        </button>
                        {this.pluginTemplates.renderBackLink("Back", false, this.getBackLink())}
                    </div>
                </div>
                {this.renderLVQuotes()}
                <Footer/>
                <GetNewQuoteModal
                    isOpen={this.state.showModal === Modals.ALLIANZ}
                    closeModal={this.closeModal}
                    navigateToPage={this.navigateToPage}
                />
                <Overlay show={this.state.loading && !this.state.showModal}/>
            </>
        );
    }

    private readonly  renderLVQuotes = () => {
        // @ts-ignore
        const QuoteButton = ({onClick, text}) => (
            <div className="get-quote-button">
                <Button onClick={onClick} className="btn btn-primary btn-next-no-arrow m-0-desktop">
                    {text}
                </Button>
            </div>
        );

        // @ts-ignore
        const QuoteHeader = ({productType}) => (
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
                <div className="product-title">LV= {productType} insurance</div>
                <div style={{textAlign: 'right'}}>
                    <b>10%</b><br/>
                    discount
                </div>
            </div>
        );
    }

    private renderQuoteChoice = () => {
        const quoteListNotEmpty = this.state.annualMotorQuotes.length > 0;
        return (
            <div className="get-quote-allianz mx-3">
                <div>
                    <div className="quote-panel-view-quotes mt-3">
                        {quoteListNotEmpty &&
                            <div>
                                {this.renderQuotes()}
                            </div>
                        }
                    </div>
                </div>
            </div>
        )
    }

    private renderQuotes() {
        const annualCarQuotes: any = [];

        // Filter quotes which have status BINDING_QUOTE
        const allBindingQuotes = this.state.annualMotorQuotes?.filter(quote => {
            return quote.interaction?.status === "BINDING_QUOTE";
        }) || [];
        const today = moment().utc().startOf('day');

        // Separate active and expired quotes based on startDate using UTC
        const activeQuotes = allBindingQuotes.filter(quote =>
            moment.utc(quote.interaction.headerInfo.startDate).isSameOrAfter(today)
        );

        const expiredQuotes = allBindingQuotes.filter(quote =>
            moment.utc(quote.interaction.headerInfo.startDate).isBefore(today)
        );

        let quotesToDisplay: Quote[] = [];
        let isDisplayingExpired = false;

        if (activeQuotes.length > 0) {
            quotesToDisplay = activeQuotes;
        } else if (expiredQuotes.length > 0) {
            expiredQuotes.sort((a, b) =>
                moment.utc(b.interaction.headerInfo.startDate).valueOf() - moment.utc(a.interaction.headerInfo.startDate).valueOf()
            );
            quotesToDisplay = [expiredQuotes[0]];
            isDisplayingExpired = true;
        }

        for (const quote of quotesToDisplay.slice(0, this.state.quoteLimit)) {
            if (quote.interaction.vehicles && quote.interaction.vehicles.length !== 0) {
                this.setQuotesContent(quote, annualCarQuotes, isDisplayingExpired);
            }
        }

        return (
            <div>
                {(annualCarQuotes && annualCarQuotes.length > 0) && (
                    <div className="journey-content your-annual-quotes">
                        <div className="journey-questions">
                            {annualCarQuotes}
                            {(quotesToDisplay.length > this.state.quoteLimit && !isDisplayingExpired) && (
                                <button type="button" className="btn btn-primary btn-next plugin"
                                        onTouchStart={this.loadMore}
                                        onMouseDown={this.loadMore}
                                        id={"show-more"}>
                                    Load more
                                </button>
                            )}

                            {this.state.showSuccessModal &&
                                <StorageSuccessModal title="Quote is deleted!"
                                                     explanationWording="Your quote has been deleted successfully"
                                                     onClose={this.navigateTo("/dashboard/")}
                                />}
                            {<ConfirmModal show={this.state.showQuoteDeleteQuestionModal}
                                           onHide={this.navigateTo("/dashboard/")}
                                           title="Are you sure you want to delete this quote?"
                                           confirmButtonText="Yes, delete quote"
                                           confirmButtonClassName="btn btn-primary btn-next-no-arrow my-2"
                                           cancelButtonText="No, keep quote"
                                           cancelButtonClassName="btn btn-white my-2"
                                           onConfirm={this.confirmQuoteDeleteAndCloseDeleteQuoteQuestionModal}>
                            </ConfirmModal>}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    private readonly setQuotesContent = (quote: Quote, productQuotes: any[], isExpired: boolean = false): void => {
        const vehicle = quote.interaction.vehicles[0];
        const engineSize: string = getVehicleEngineSize(vehicle);

        productQuotes.push(
            <div className="your-quotes-panel" key={quote.interaction._id}>

                {isExpired && (
                    <div className="quote-expired-banner p-3 mb-4">
                        <div className="d-flex align-items-start">
                            <i className="icon-warning mr-2"></i>
                            <div>
                                <div className="mb-1">
                                    <strong>Quote expired.</strong>
                                </div>
                                <div>
                                    The start date of this quote is now in the past. Please{' '}
                                    <button
                                        type="button"
                                        onClick={this.onUpdateExpiredQuoteClick(quote)}
                                        className="text-dark text-decoration-underline border-0 bg-transparent p-0"
                                    >
                                        select a new policy start date
                                    </button>
                                    {' '}to get an updated quote.
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="h3">{vehicle.make}, {vehicle.model}</div>
                <div className="number-plate-preview mb-3">{vehicle.registrationNumber}</div>
                <div className="car-details mb-3">
                    <p className="mb-0">{engineSize ? engineSize + "cc, " : ""}
                        {getVehicleNumberOfDoors(vehicle)} dr,&nbsp;
                        {this.resolveLookupValue("vehicleTransmission", vehicle.transmission)},&nbsp;
                        {this.resolveLookupValue("vehicleFuelType", getInteractionVehicleFuelType(vehicle.fuelType, vehicle.fuelTypeFull))}
                    </p>
                    <p className="mb-0">Manufactured in {vehicle.year}</p>
                </div>

                <div id="price" className="price premium-amount">
                    <p className="mb-0 mt-2 small">Price</p>
                    <div
                        className="h3 muli-bold-16 m-0">{getAmountWithDecimalDigits(quote.interaction.factors.amountToPay)}</div>
                    <p className="mb-0 mt-2 small"> / year*</p>
                </div>
                <div className="mb-3">
                    <div className="mb-2 muli-bold-16">Quote created
                        on: <span>{moment(quote.interaction.createdOn).format(this.DATE_FORMAT)}</span></div>
                </div>
                {InteractionHelper.isMonthlyQuoteAllowed(quote.interaction) && <div className="mb-3">
                    <p className="muli-bold-16 mb-1">Monthly: <span>{getAmountWithDecimalDigits(quote.interaction.factors.monthlyPremium)}</span>
                    </p>
                    <p className="m-0">{getAmountWithDecimalDigits(quote.interaction.paymentDetails.instalmentPlan.totalAmount)} in
                        total,</p>
                    <p className="m-0">{this.calculatePriceDifference(quote.interaction)} more expensive to pay
                        monthly</p>
                    <p className="m-0">All figures include interest charge and Insurance Premium Tax</p>
                </div>}
                <div className="mb-3">
                    <p className="mb-2 muli-bold-16">Start
                        date: <span>{moment(quote.interaction.headerInfo.startDate).format(this.DATE_FORMAT)}</span>
                    </p>
                </div>
                <div className="mt-3">
                    <div className="h3">Drivers</div>
                    {this.getDrivers(vehicle, quote)}
                </div>
                <div className="d-flex button-container w-100">
                    <div className="form-group">
                        {isExpired ? (
                            <button type="button"
                                    className="btn btn-blue-dashboard m-auto"
                                    onClick={() => this.navigateToUpdateQuotePage(quote)}
                                    id={"updateQuote" + quote.quoteId}>
                                Update quote
                            </button>
                        ) : (
                            <button type="button"
                                    className="btn btn-blue-dashboard m-auto"
                                    onClick={() => this.fetchDriveAwayInteraction(quote)}
                                    id={"viewAndEditQuote" + quote.quoteId}>
                                Purchase the quote
                            </button>
                        )}
                    </div>

                    <div className="form-group pl-0 pl-md-5">
                        <button type="button"
                                className="btn btn-white-dashboard m-auto"
                                onClick={() => this.showQuoteDeleteQuestionModal(quote.interaction._id)}
                                id={"deleteQuote" + quote.quoteId}>
                            Delete quote
                        </button>
                    </div>
                </div>
                <div>
                    <p className="mt-3">* All figures include Insurance Premium Tax</p>
                </div>
            </div>
        );
    }

    private navigateToUpdateQuotePage(quote: Quote): void {
        const process = quote.interaction.process;
        const url = `/journey/show/product/AnnualMotorInsurance/process/${process}/updateExpiredQuote/${quote.interaction._id}?auth`;
        this.props.navigate(url);
    }

    private isAllianz(productType: AllProducts): boolean {
        return (Object.values(ProductsEnum) as AllProducts[]).includes(productType);
    }

    private readonly openModal = (productType: AllProducts) => {
        const showModal = this.isAllianz(productType) ? Modals.ALLIANZ : Modals.LV;

        this.setState({
            showModal,
            selectedProduct: productType
        })
    }

    private readonly closeModal = () => {
        this.setState({showModal: undefined, selectedProduct: undefined})
    }

    private readonly navigateToPage = () => {
        const product = this.state.selectedProduct;
        const entryPage: string = "dashboardEntryPage";

        if (product) {
            this.props.navigate(`${productHelper.getNewBusinessUrl(product)}/${entryPage}`);
        }
    }

    private readonly calculatePriceDifference = (interaction: any): string => {
        const amountDifference: number = interaction.paymentDetails.instalmentPlan.creditCost;
        return getAmountWithDecimalDigits(amountDifference);
    }

    private getQuotesByProduct(quotes: Quote[], product: string): Quote[] {
        return quotes?.filter((quote) => quote?.product === product);
    }

    private getDrivers(vehicle: any, quote: Quote) {
        const drivers: any = [];
        if (vehicle.mainDriverId) {
            drivers.push(
                <div key={vehicle.mainDriverId}>{getDriverInfo(vehicle.mainDriverId)} (main driver)</div>
            );
        }

        for (const driverId of vehicle.driverIds) {
            if (quote.interaction.additionalDrivers && quote.interaction.additionalDrivers.length !== 0
                && driverId !== vehicle.mainDriverId) {
                drivers.push(
                    <div className="mt-3" key={driverId}>
                        {getDriverInfo(driverId)}
                    </div>
                );
            }
        }
        return drivers;

        function getDriverInfo(driverId: any) {
            if (driverId === quote.interaction.policyHolder.personId) {
                return (
                    <span>{quote.interaction.policyHolder.firstName} {quote.interaction.policyHolder.lastName}</span>
                );
            }
            let driverInfo;
            for (const driver of quote.interaction.additionalDrivers) {
                if (driverId === driver.personId) {
                    driverInfo =
                        <span>{driver.firstName} {driver.lastName}</span>
                }
            }
            return driverInfo;
        }
    }

    private resolveLookupValue(lookupName: string, lookupId: string): string {
        if (!lookupId || !this.state?.lookup[lookupName]) {
            return "";
        }
        const listItem: ListItem | undefined = this.state.lookup[lookupName].find((listItem: ListItem) => `${listItem.id}` === `${lookupId}`);
        if (!listItem) {
            throw new Error(`Error: no item with id '${lookupId}' found in lookup '${lookupName}'`);
        }
        return listItem.value;
    }

    private fetchLookupValues(lookupName: string) {
        this.lookupService.fetchLookupValues(lookupName, "en-UK").subscribe((lookupValues: ListItem[]) => {
            this.setState(prevState => ({
                lookup: {
                    ...prevState.lookup,
                    [lookupName]: lookupValues
                }
            }));
        }, () => {
            throw new Error(`Error: failed to fetch lookup values for '${lookupName}' lookup`);
        });
    }

    private readonly loadMore = (): void => {
        this.setState(prevState => ({
            quoteLimit: prevState.quoteLimit + 3
        }))
    }

    private fetchDriveAwayInteraction(quote: Quote) {
        if (quote.interaction.driveAwayInteractionId != null) {
            this.propositionEngine.fetchInteractionByEmailAndDob(ProductsEnum.WOLF_MOTOR,
                quote.interaction.driveAwayInteractionId,
                quote.interaction.policyHolder.email, quote.interaction.policyHolder.dateOfBirth).subscribe(
                (driveAwayQuoteInteraction: any) => {
                    this.setState({driveAwayQuoteInteraction});
                    const purchasePolicy = this.getViewAndEditDetailsLink(quote, driveAwayQuoteInteraction);
                    this.props.navigate(purchasePolicy);
                });
        } else {
            const process = this.props.isActivePoliciesPresent ?
                `${quote.interaction.process}/summary` : "nb/summary";
            const redirectToAnnualProduct =
                `/journey/show/product/AnnualMotorInsurance/process/${process}/${quote.interaction._id}?auth`;
            this.props.navigate(redirectToAnnualProduct);
        }
    }

    private readonly getViewAndEditDetailsLink = (quote: Quote, driveAwayQuoteInteraction : any): string => {

        const driveAwayQuoteStartDate = driveAwayQuoteInteraction?.headerInfo?.startDate;
        const todayDate = moment().startOf('day').utc();
        const notExpiredDriveAwayQuote: boolean = moment(driveAwayQuoteStartDate).isAfter(todayDate);

        const isAnnualInDriveAway :boolean = quote.interaction.uistate.isAnnualCoverInteractionInDriveAwayJourney;
        const isDriveAwaySigned: boolean = driveAwayQuoteInteraction?.status === "AGREEMENT";

        let process: string = this.props.isActivePoliciesPresent ? quote.interaction.process + "/summary" : "nb/summary";
        const product: string = isAnnualInDriveAway && notExpiredDriveAwayQuote && !isDriveAwaySigned
            ? "DriveAwayInsurance" : "AnnualMotorInsurance";

        return "/journey/show/product/" + product + "/process/" + process + "/" + quote.interaction._id + "?auth";
    }

    private navigateTo(link: string) {
        return () => {
            window.location.href = link;
        }
    }

    private readonly showQuoteDeleteQuestionModal = (interactionId: string): void => {
        this.setState({
            interactionIdToUpdate: interactionId,
            showQuoteDeleteQuestionModal: true
        });
    }

    private readonly confirmQuoteDeleteAndCloseDeleteQuoteQuestionModal = (): void => {
        const interactionId: string = this.state.interactionIdToUpdate;
        if (interactionId == "") {
            return
        } else {
            this.customerPortalService.cancelQuote(interactionId).subscribe(() => {
                this.setState({
                    showQuoteDeleteQuestionModal: false,
                    showSuccessModal: true
                })
            })
        }
    }

    private getBackLink(): string {
        return "/dashboard";
    }

    private readonly onUpdateExpiredQuoteClick = (quote: Quote) => (): void => {
        this.navigateToUpdateQuotePage(quote);
    };

}

interface IGetNewQuoteModalProps {
    isOpen: boolean;
    closeModal: () => void;
    navigateToPage: (event: any) => void;
}

const GetNewQuoteModal: React.FC<IGetNewQuoteModalProps> = ({isOpen, closeModal, navigateToPage}) => (
    <Modal className="custom-info-modal" isOpen={isOpen} toggle={closeModal} id="getNewQuoteModal">
        <ModalHeader toggle={closeModal}>
            <div className="d-flex">
                <img src={`/${resourcesHelper.THEME_PATH}/img/info-popup.svg`} alt="Info icon"/>
                <span
                    className="ml-3 text-blue-2 fs-20 font-weight-medium">Before continuing to get a new quote..</span>
            </div>
        </ModalHeader>
        <ModalBody className="pt-0">
            <p className="font-weight-semi-bold">To get new policies in the existing account please check the
                following:</p>
            <p className="font-weight-extra-light">● The new policy use the same email address as the existing
                policy;</p>
            <p className="font-weight-extra-light">● The policyholder must have the same name and date of birth as on
                the existing policy;</p>
            <p className="font-weight-extra-light">● The policyholder must live at the same address as on the existing
                policy.</p>
        </ModalBody>
        <ModalFooter className="my-2 w-100">
            <button type="button" className="btn btn-primary btn-next-no-arrow w-100" onClick={navigateToPage}
                    id="accept-btn">Ok, I understand
            </button>
        </ModalFooter>
    </Modal>
);

export const GetNewQuotePage = withRouter(GetNewQuotePagePlain)
